import { useContext, useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import ExcelJS from 'exceljs';
import { collectionGroup, doc, getDoc, getDocs, getFirestore, or, query, where } from 'firebase/firestore';
import { CustomClaims, Customer, Service, ServiceStatusCode } from '../types';
import { GLOBAL_DPM_PORTFOLIO_DB_ID, portfolioDoneSummary } from '../portfolio_done_summary';
import { ConfigContext, MyUserDataContext } from '../contexts';

/* One click, no filters: every DONE Global DPM (multi_assets) portfolio request as an
   Excel table (every column sortable/filterable, newest executed first) with the same
   7 portfolio-only fields as the "portfolio done" email (portfolio_done_summary.ts).
   No KYC information. */

// exceljs writes a Date as its UTC wall-clock; shift so Excel shows Kuwait time (no DST there)
const KUWAIT_OFFSET_MS = 3 * 60 * 60 * 1000;

const GlobalDpmExportButton = () => {
    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);
    const riskLevels = configContext.portfolio_service?.[GLOBAL_DPM_PORTFOLIO_DB_ID]?.management_options?.[0]?.risk_levels;
    const userClaims = (userContext.firebaseTokenResult?.claims.customClaims as CustomClaims) || {};
    const db = getFirestore();
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        try {
            let q = query(
                collectionGroup(db, 'services'),
                where('serviceType', '==', 'portfolio'),
                where('portfolioOpeningRequest.portfolio_db_id', '==', GLOBAL_DPM_PORTFOLIO_DB_ID),
                where('tags.serviceStatus.code', '==', ServiceStatusCode.done),
            );
            // same segment scoping as the services excel export
            if (userClaims.availableSegments) {
                q = query(q, or(where('rm.segment', 'in', userClaims.availableSegments), where('customerObj.segment', 'in', userClaims.availableSegments)));
            } else if (userClaims.segment) {
                q = query(q, or(where('rm.segment', '==', userClaims.segment), where('customerObj.segment', '==', userClaims.segment)));
            }
            const snap = await getDocs(q);
            const summaries = await Promise.all(snap.docs.map(async (d) => {
                const service = { ...(d.data() as Service), id: d.id };
                const customer = service.customerObj?.id
                    ? ((await getDoc(doc(db, 'customers', service.customerObj.id))).data() as Customer | undefined)
                    : undefined;
                return portfolioDoneSummary(service, customer ?? service.customerObj, window.location.host, riskLevels);
            }));
            if (summaries.length === 0) {
                notifications.show({ title: 'Global DPM export', message: 'No done Global DPM portfolios found', color: 'yellow' });
                return;
            }
            // latest executed first; never-executed (no Geneva timestamp) last
            summaries.sort((a, b) => (b.executedAt?.getTime() ?? 0) - (a.executedAt?.getTime() ?? 0));

            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet('Global DPM');
            const headers = ['Name', 'Civil ID / Commercial Register', 'Portfolio ID', 'Amount (USD)', 'Risk Tolerance', 'Request Link', 'Executed Date'];
            ws.addTable({
                name: 'GlobalDpm',
                ref: 'A1',
                headerRow: true,
                style: { theme: 'TableStyleMedium2', showRowStripes: true },
                columns: headers.map((name) => ({ name, filterButton: true })),
                rows: summaries.map((s) => [
                    s.name,
                    s.idNumber,
                    s.portfolioId,
                    s.amountUsd,
                    s.riskTolerance,
                    s.link,
                    s.executedAt ? new Date(s.executedAt.getTime() + KUWAIT_OFFSET_MS) : '',
                ]),
            });
            summaries.forEach((s, i) => { ws.getCell(i + 2, 6).value = { text: s.link, hyperlink: s.link }; });
            ws.getColumn(4).numFmt = '#,##0.00'; // numeric → numeric sort in Excel
            ws.getColumn(7).numFmt = 'dd/mm/yyyy hh:mm'; // real date cells → chronological sort in Excel
            ws.columns.forEach((c) => { c.width = 28; });

            const blob = new Blob([await wb.xlsx.writeBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `global_dpm_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Global DPM export failed', message: 'Check console', color: 'red' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button variant="light" leftSection={<IconDownload size={16} />} loading={busy} onClick={download}>
            Global DPM export
        </Button>
    );
};

export default GlobalDpmExportButton;
